import { tokenChipHtml, tokenLabel, MODE_ONE, AUDIENCE_VENDORS, AUDIENCE_AGENTS } from './merge';

const chip = (key) => tokenChipHtml(key, tokenLabel(key));

const SUMMIT_SITE = 'https://summitre.io';

/** Summit's own pages, which the client wants offered in every letter. */
export const SUMMIT_LINKS = [
    { label: 'Website', text: 'SummitRE.io', url: SUMMIT_SITE },
    { label: 'Results', text: 'Results', url: SUMMIT_SITE + '/pages/results' },
    { label: 'Team', text: 'Team', url: SUMMIT_SITE + '/pages/teams' },
];

/**
 * The client's five letters in their own words, set back in sentence case: the
 * ticket arrived with every word capitalised. They gave no subject lines.
 *
 * `propertyMode` is where the property tokens start.
 */
export const TEMPLATES = [
    {
        id: 'buyside-owns-similar',
        group: 'Buyside',
        name: 'Owns Similar Properties',
        propertyMode: MODE_ONE,
        bodyHtml:
            '<p>' + chip('OWNER_FIRST_NAME') + ',</p>' +
            '<p>I understand that you own the ' + chip('PROPERTY_NAME') + ' in ' + chip('CITY_STATE') +
            '. I\'m currently marketing the following asset and thought it might be of interest to you.</p>' +
            '<p>Please click the following link to download the marketing package:</p>' +
            '<p>' + chip('LISTING_LINK') + '</p>' +
            '<p>If you have any questions or further interest, please don\'t hesitate to reach out.</p>' +
            '<p>I look forward to hearing your thoughts.</p>' +
            '<p>' + chip('AGENT_SIGNATURE') + '</p>',
    },
    {
        id: 'buyside-general',
        group: 'Buyside',
        name: 'General',
        bodyHtml:
            '<p>' + chip('OWNER_FIRST_NAME') + ',</p>' +
            '<p>I\'m currently marketing the ' + chip('LISTING_NAME') + ' and thought you might have an interest.</p>' +
            '<p>The basic info is as follows:</p>' +
            '<p>Price: ' + chip('LISTING_PRICE') + '<br>NOI: ' + chip('LISTING_NOI') +
            '<br>Cap Rate: ' + chip('LISTING_CAP_RATE') + '</p>' +
            '<p>' + chip('LISTING_HIGHLIGHTS') + '</p>' +
            '<p>Please click the following link to download the full marketing package:</p>' +
            '<p>' + chip('LISTING_LINK') + '</p>' +
            '<p>If you have any questions or further interest, please let me know.</p>' +
            '<p>I look forward to hearing your thoughts.</p>' +
            '<p>' + chip('AGENT_SIGNATURE') + '</p>',
    },
    {
        id: 'buyside-coming-to-market',
        group: 'Buyside',
        name: 'Coming to Market',
        bodyHtml:
            '<p>' + chip('OWNER_FIRST_NAME') + ',</p>' +
            '<p>We\'re bringing ' + chip('LISTING_NAME') + ' in ' + chip('LISTING_CITY_STATE') +
            ' to market next week and wanted to give you an early look before it officially launches.</p>' +
            '<p>A few key details are below:</p>' +
            '<p>Price: ' + chip('LISTING_PRICE') + '<br>NOI: ' + chip('LISTING_NOI') +
            '<br>Cap Rate: ' + chip('LISTING_CAP_RATE') + '</p>' +
            '<p>' + chip('LISTING_HIGHLIGHTS') + '</p>' +
            '<p>Please click the following link to download the full marketing package: ' + chip('LISTING_LINK') + '</p>' +
            '<p>If this opportunity is of interest or you have any questions, please don\'t hesitate to reach out. ' +
            'I\'d be happy to provide additional information.</p>' +
            '<p>I look forward to hearing your thoughts.</p>' +
            '<p>' + chip('AGENT_SIGNATURE') + '</p>',
    },
    {
        id: 'listing-drove-by',
        group: 'Listing',
        name: 'Drove By',
        propertyMode: MODE_ONE,
        bodyHtml:
            '<p>' + chip('OWNER_FIRST_NAME') + ',</p>' +
            '<p>I recently drove by your ' + chip('PROPERTY_NAME') + ' in ' + chip('CITY_STATE') +
            ' and wanted to see if you had interest in selling this asset. ' +
            'Please let me know if you would consider selling it.</p>' +
            '<p>' + chip('AGENT_SIGNATURE') + '</p>',
    },
    {
        id: 'listing-follow-up',
        group: 'Listing',
        name: 'Follow Up',
        bodyHtml:
            '<p>' + chip('OWNER_FIRST_NAME') + ',</p>' +
            '<p>As a follow up to our call, our team at Summit RE specializes exclusively in Texas retail investment sales. This singular focus ' +
            'allows us to close more retail transactions, maximize value for our clients, and maintain one of the most ' +
            'extensive databases of qualified buyers for Texas retail properties in the country - period.</p>' +
            '<p>Our national technology platform, combined with deep local market knowledge, allows us to connect ' +
            'sellers with highly motivated 1031 exchange buyers from across the country, as well as active local ' +
            'investors seeking to deploy capital into income-producing real estate. To date, we have closed more than ' +
            '$6.5 billion in real estate transactions across more than 1,700 transactions, consistently ranking Summit RE ' +
            'among the leading brokerage firms in the country. This year alone, we have more than half a billion dollars ' +
            'in properties closed and/or under contract, demonstrating the depth of investor demand for quality ' +
            'investment real estate.</p>' +
            '<p>If you are considering a sale, the opportunity is clear: strong buyer demand, combined with our extensive ' +
            'buyer database and national reach, will drive the price to the height of the market and allow us to find ' +
            'the buyer with the most aggressive terms and the highest probability of closing.</p>' +
            '<p>For more information, please visit <a href="' + SUMMIT_SITE + '">SummitRE.io</a> to explore our current ' +
            'listings, recent sales, awards and recognitions, transaction history, and team.</p>' +
            '<p>My contact information is below. Please don\'t hesitate to reach out with any questions or if you\'d like ' +
            'to discuss the current market and the potential value of your property.</p>' +
            '<p>I look forward to hearing from you.</p>' +
            '<p>' + chip('AGENT_SIGNATURE') + '</p>',
    },
];

const paragraph = (text) => '<p>' + text + '</p>';

/**
 * Where a letter to someone on a contact list starts: the client's "None -
 * Write Your Own", a greeting and a signature with room between them.
 */
export const CONTACT_STARTER_BODY =
    paragraph(chip('CONTACT_FIRST_NAME') + ',') +
    '<p><br></p>' +
    paragraph(chip('AGENT_SIGNATURE'));

/**
 * The client's letters to vendors (schools, coaches, fraternities), in their
 * own words and set back in sentence case. The fraternity letter arrived as
 * one run-on block with no greeting; it is split where the coaches' letter
 * breaks and opens the same way. They gave no subject lines. The client names
 * these whole, so there is no group.
 */
export const VENDOR_TEMPLATES = [
    {
        id: 'vendor-frats-hiring',
        name: 'Frats - Hiring',
        bodyHtml:
            paragraph(chip('CONTACT_FIRST_NAME') + ',') +
            paragraph("I hope you're doing well.") +
            paragraph("I wanted to reach out because Summit RE is growing, and we're looking for high-quality people to " +
                'join our team. I thought you might be willing to share this opportunity with your fraternity brothers ' +
                'and alumni network.') +
            paragraph("We built Summit RE from the ground up, and we're continuing to grow our team and expand our " +
                'business. We specialize in commercial real estate investment sales, with offices in Dallas, Houston ' +
                "and SoCal, but more importantly, we're building a company around great people, a strong culture, hard " +
                'work, and long-term opportunity.') +
            paragraph('At Summit RE, we focus on hiring high-quality people who are willing to work hard and make ' +
                "sacrifices in the short term for long-term success. We don't expect everyone to have a background in " +
                'commercial real estate. We value character, work ethic, attitude, coachability, and willingness to ' +
                'learn. We believe the right people, given the right opportunity and environment, can accomplish ' +
                'extraordinary things.') +
            paragraph('I know fraternities develop many of these same qualities: leadership, teamwork, accountability, ' +
                "loyalty, and a commitment to something bigger than yourself. That's why I thought this could be a " +
                'great opportunity to share with your network. If you know someone who might be a good fit, whether ' +
                "they're graduating soon, recently graduated, or considering a career change, we'd love to connect " +
                'with them.') +
            paragraph('Would you mind passing this along to your brothers and alumni? Anyone interested can reach out ' +
                'to Anna, our Human Resources Coordinator, or me directly. Thanks for your help, and I appreciate you ' +
                'spreading the word!') +
            paragraph(chip('AGENT_SIGNATURE')),
    },
    {
        id: 'vendor-hiring-coaches',
        name: 'Hiring - Coaches',
        bodyHtml:
            paragraph(chip('CONTACT_FIRST_NAME') + ',') +
            paragraph("I hope you're doing well.") +
            paragraph("I wanted to reach out because Summit RE is growing, and we're looking for high-quality people to " +
                'join our team. I thought you might be willing to share this opportunity with your athletes and alumni.') +
            paragraph("We built Summit RE from the ground up, and we're continuing to grow our team and expand our " +
                'business. We specialize in commercial real estate investment sales, with offices in Dallas, Houston, ' +
                "and Southern California. More importantly, we're building a company around great people, a strong " +
                'culture, hard work, and long-term opportunity.') +
            paragraph('At Summit RE, we believe success comes from hiring people who are willing to work hard, make ' +
                'sacrifices in the short term, and stay committed to long-term success.') +
            paragraph("You don't need a background in commercial real estate to be successful here. We value character, " +
                'work ethic, attitude, coachability, accountability, and a willingness to learn. We believe the right ' +
                'people, given the right opportunity and environment, can accomplish extraordinary things.') +
            paragraph('I know sports instills many of the same qualities we value at Summit RE: discipline, resilience, ' +
                'teamwork, accountability, coachability, and the drive to compete. Our founder was a college hockey ' +
                'player and captain of the 1999 University of Maine Black Bears NCAA Division I National Championship ' +
                "team, and many of our current agents are former Division I athletes. We've seen firsthand how the " +
                'mindset and work ethic developed through athletics can translate into success in business. We believe ' +
                'there are tremendous opportunities for athletes who are willing to channel that same competitive ' +
                'drive into building a meaningful career.') +
            paragraph("If you know someone who might be a good fit, whether they're graduating soon, recently graduated, " +
                "or considering a career change, we'd love to connect with them.") +
            paragraph('Would you mind passing this along to your athletes and alumni? Anyone interested can reach out to ' +
                'Anna, our Human Resources Coordinator, or me directly.') +
            paragraph('Thanks for your help and for spreading the word. I really appreciate it!') +
            paragraph(chip('AGENT_SIGNATURE')),
    },
];

/** The client's letter to other brokers, recruiting them to Summit. */
export const AGENT_TEMPLATES = [
    {
        id: 'agent-hiring-agents',
        name: 'Hiring - Agents',
        bodyHtml:
            paragraph(chip('CONTACT_FIRST_NAME') + ',') +
            paragraph("I hope you're doing well.") +
            paragraph("I wanted to reach out because Summit RE continues to grow, and we're always interested in " +
                'connecting with talented commercial real estate professionals who want to take their careers to the ' +
                'next level and be part of a winning culture.') +
            paragraph("We built Summit RE from the ground up, and we're building something different. With offices in " +
                "Dallas, Houston, and Southern California, we've invested heavily in our technology, proprietary " +
                'database, AI-powered platform, and infrastructure to give our agents a competitive advantage.') +
            paragraph('Our goal is simple: eliminate the mundane, time-consuming aspects of the business so our agents ' +
                'can focus on what actually drives revenue. Rather than spending hours building databases, researching ' +
                'ownership, searching for on-market and sold comparables, or underwriting deals, our agents have access ' +
                'to technology and accurate, actionable information at their fingertips that helps them work smarter ' +
                'and more efficiently. This allows them to spend more time developing relationships, connecting buyers ' +
                'and sellers, closing transactions, and delivering exceptional service to their clients. Ultimately, we ' +
                'believe a better platform allows our agents to build bigger businesses and increase their earning ' +
                'potential.') +
            paragraph('But technology is only part of the equation. We believe a great company is built around great ' +
                'people, a strong culture, collaboration, accountability, and a shared commitment to delivering ' +
                'results. We operate as one team. We believe in building each other up, sharing knowledge, and ' +
                'creating opportunities for our agents rather than having them compete against one another internally. ' +
                'We want our people focused on winning business and serving clients, not fighting for position within ' +
                'their own firm.') +
            paragraph("We're looking for driven, entrepreneurial professionals who want to grow their business, maximize " +
                'their potential, and be part of building something meaningful. We value people who take ownership, ' +
                'think like business owners, embrace new ideas, and understand that long-term success comes from hard ' +
                'work, discipline, and consistently delivering results.') +
            paragraph('Summit RE offers the opportunity to join a growing company where your contributions matter, your ' +
                'ideas are valued, and you can help shape the future of the business. We\'re building a platform ' +
                'designed to give our agents the technology, resources, infrastructure, and team support to compete at ' +
                'the highest level while maintaining the entrepreneurial spirit that makes this business rewarding.') +
            paragraph("I've always believed that the right environment, team, and platform can make a significant " +
                "difference in what someone can accomplish. If you're already successful, imagine what you could " +
                "achieve with the right tools, support, and infrastructure behind you. And if you're looking to take " +
                "your business to another level, I'd welcome the opportunity to show you what we're building at " +
                'Summit RE.') +
            paragraph("If you're open to a conversation about your business, your goals, and what you want the next " +
                "chapter of your career to look like, I'd welcome the opportunity to connect. No pressure or " +
                "expectations, just a conversation to see if there's a fit.") +
            paragraph('Best,') +
            paragraph(chip('AGENT_SIGNATURE')),
    },
];

/** The letters offered to a merge for this audience. */
export function templatesFor(audience) {
    if (audience === AUDIENCE_VENDORS) return VENDOR_TEMPLATES;
    if (audience === AUDIENCE_AGENTS) return AGENT_TEMPLATES;
    return TEMPLATES;
}

export function templateById(id) {
    return TEMPLATES.concat(VENDOR_TEMPLATES, AGENT_TEMPLATES).filter((template) => template.id === id)[0] || null;
}

/** "Buyside - General", the way the client names them; a letter with no group is named whole. */
export function templateTitle(template) {
    return template.group ? template.group + ' - ' + template.name : template.name;
}
